import { BottomSheet, mergeSxProps, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack, SxProps } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { PickerSelectionState } from "@mui/x-date-pickers/internals/hooks/usePicker";
import { useCallback, useEffect, useState } from "react";

import { BottomSheetHeader } from "../components/BottomSheetHeader";
import { Button } from "../components/Button";
import { DialogFooter } from "../components/DialogFooter";

interface DatePickerInputBottomSheetProps {
  modalState: UseModalState;
  initialDate?: Date;
  disableFuture?: boolean;
  minDate?: Date;
  maxDate?: Date;
  shouldAutoConfirmOnSelect?: boolean;
  shouldHideCloseButton?: boolean;
  onConfirm: (date: Date) => void;
  sx?: SxProps<Theme>;
}

export function DatePickerInputBottomSheet(props: DatePickerInputBottomSheetProps) {
  const {
    modalState,
    initialDate,
    disableFuture,
    minDate,
    maxDate,
    shouldAutoConfirmOnSelect = false,
    shouldHideCloseButton = false,
    onConfirm,
    sx,
  } = props;

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);

  useEffect(() => {
    if (modalState.modalIsOpen) {
      setSelectedDate(initialDate);
    }
  }, [modalState.modalIsOpen, initialDate]);

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  return (
    <BottomSheet
      disableSwipeArea
      footer={
        shouldAutoConfirmOnSelect ? undefined : (
          <DialogFooter orientation="vertical">
            <Button
              fullWidth
              variant="contained"
              disabled={!isDefined(selectedDate)}
              onClick={() => {
                if (!selectedDate) {
                  return;
                }

                onConfirm(selectedDate);
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        )
      }
      header={shouldHideCloseButton ? undefined : <BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
    >
      <DialogContent>
        <Stack width="100%" spacing={4} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              disableFuture={disableFuture}
              minDate={minDate}
              maxDate={maxDate}
              value={selectedDate}
              sx={mergeSxProps(
                (theme) => ({
                  width: "100%",
                  "& .MuiPickersCalendarHeader-root": {
                    paddingLeft: theme.spacing(4),
                    paddingRight: 0,
                  },
                  "& .MuiDayCalendar-header": {
                    justifyContent: "space-between",
                  },
                  "& .MuiDayCalendar-weekContainer": {
                    justifyContent: "space-between",
                  },
                  "& .MuiYearCalendar-root": {
                    marginLeft: "auto",
                    marginRight: "auto",
                  },
                }),
                sx
              )}
              onChange={(newDate: Date | undefined, selectionState?: PickerSelectionState) => {
                if (isDefined(newDate)) {
                  setSelectedDate(newDate);
                  if (shouldAutoConfirmOnSelect && selectionState === "finish") {
                    onConfirm(newDate);
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
